import { useState, useEffect } from 'react';
import { Asset } from '@/utils/collectionsGraphQL';
import { supabase } from '@/integrations/supabase/client';

export function useAssetById(assetId: string | null) {
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!assetId) {
      setAsset(null);
      setLoading(false);
      return;
    }

    const fetchAsset = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use direct database query instead of GraphQL
        const { data, error: dbError } = await supabase
          .from('app_assets')
          .select(`
            *,
            collection:collection_id (
              id,
              name
            ),
            page:page_id (
              id,
              pricing_page_url,
              company:company_id (
                id,
                slug,
                logo_url
              )
            )
          `)
          .eq('id', assetId)
          .single();

        if (dbError) throw dbError;

        // Transform the data to match the Asset type
        const transformedAsset: Asset = {
          ...data,
          company_slug: data.page?.company?.slug,
          logo_url: data.page?.company?.logo_url,
        };

        setAsset(transformedAsset);
      } catch (err) {
        console.error('Error fetching asset:', err);
        setError('Failed to load asset');
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [assetId, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  return { asset, loading, error, refetch };
}